/**
 * Create Key Modal
 *
 * Modal for creating a new API key.
 *
 * @module components/keys/CreateKeyModal
 */

import { useState, useEffect } from 'react';
import { Modal, Button, Input } from '@/components/ui';

// =============================================================================
// Types
// =============================================================================

export interface CreateKeyModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (label: string) => Promise<void>;
  /** Pre-fill the label */
  defaultLabel?: string;
  /** Current count of API keys */
  keyCount: number;
  /** Maximum allowed keys for the plan */
  maxKeys: number;
}

// =============================================================================
// Component
// =============================================================================

export function CreateKeyModal({
  open,
  onClose,
  onSubmit,
  defaultLabel = '',
  keyCount,
  maxKeys,
}: CreateKeyModalProps) {
  const [label, setLabel] = useState(defaultLabel);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens or defaults change
  useEffect(() => {
    if (open) {
      setLabel(defaultLabel);
      setError(null);
    }
  }, [open, defaultLabel]);

  const atLimit = maxKeys !== -1 && keyCount >= maxKeys;

  const handleSubmit = async () => {
    if (!label.trim()) {
      setError('Please enter a name for your API key.');
      return;
    }

    if (atLimit) {
      setError('You have reached the maximum number of API keys for your plan.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit(label.trim());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create API key.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create API Key"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading} disabled={atLimit}>
            Create Key
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Label Input */}
        <Input
          label="Key Name"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Production Server"
          maxLength={50}
          error={error ? error : undefined}
          autoFocus
        />
      </div>
    </Modal>
  );
}